import { Component } from '@theme/component';
import { getWishlist, removeFromWishlist, WishlistUpdateEvent } from '@theme/wishlist';

/**
 * Renders the wishlist page's product grid entirely client-side: it reads saved handles from
 * localStorage, fetches each product's full product-card via Shopify's Section Rendering API,
 * and mounts the perfected product cards.
 *
 * It provides:
 * 1. Optimistic UI removals so clicking remove smoothly fades and deletes ONLY that specific card.
 * 2. "Move to Cart" behavior: clicking "Add to Cart" or quick-add bag icon on the wishlist page
 *    adds the item to cart and automatically removes it from the wishlist with a smooth exit.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} grid
 * @property {HTMLElement} empty
 * @property {HTMLTemplateElement} [cardTemplate]
 *
 * @extends {Component<Refs>}
 */
class WishlistGrid extends Component {
  requiredRefs = ['grid', 'empty'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(WishlistUpdateEvent.eventName, this.onWishlistUpdate);
    this.addEventListener('click', this.handleAddToCart);
    this.addEventListener('submit', this.handleAddToCart);
    this.load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WishlistUpdateEvent.eventName, this.onWishlistUpdate);
    this.removeEventListener('click', this.handleAddToCart);
    this.removeEventListener('submit', this.handleAddToCart);
  }

  get moneyFormat() {
    return this.dataset.moneyFormat || '{{ amount }}';
  }

  /**
   * "Move to Cart" behavior: When an item is added to cart from the wishlist page,
   * allow the AJAX cart addition to proceed, then automatically remove it from the wishlist.
   * @param {Event} event
   */
  handleAddToCart = (event) => {
    const trigger = event.target.closest(
      '.product-card__atc-btn, .product-card__quick-add-btn, form[data-type="add-to-cart-form"]'
    );
    if (!trigger) return;

    // Do not trigger if button or form submission is disabled (e.g. sold out)
    if (trigger.disabled || (trigger instanceof HTMLButtonElement && trigger.disabled)) return;
    const atcBtn = trigger.matches('.product-card__atc-btn') ? trigger : trigger.querySelector('.product-card__atc-btn');
    if (atcBtn && atcBtn.disabled) return;

    const card = trigger.closest('product-card, .product-card');
    if (!card) return;

    const wishlistBtn = card.querySelector('wishlist-button, .js-wishlist-btn');
    const handle =
      card.getAttribute('data-handle') ||
      wishlistBtn?.getAttribute('data-handle');

    if (!handle && !wishlistBtn) return;

    // Allow standard AJAX cart logic to initiate cleanly, then trigger automatic wishlist removal
    setTimeout(() => {
      if (handle) {
        removeFromWishlist(handle);
      } else if (wishlistBtn && typeof wishlistBtn.click === 'function') {
        wishlistBtn.click();
      }
    }, 120);
  };

  /**
   * Optimistically removes or updates cards on wishlist events without re-fetching or flashing the whole grid.
   * @param {WishlistUpdateEvent} event
   */
  onWishlistUpdate = (event) => {
    const { changedHandle, added } = event.detail || {};

    // 1. Optimistic removal: animate out ONLY the specific card and remove it from the DOM
    if (!added && changedHandle) {
      const card =
        this.refs.grid.querySelector(`product-card[data-handle="${changedHandle}"]`) ||
        this.refs.grid.querySelector(`[data-handle="${changedHandle}"]`) ||
        this.refs.grid
          .querySelector(`wishlist-button[data-handle="${changedHandle}"]`)
          ?.closest('product-card, .product-card, article');

      if (card) {
        card.style.transition = 'opacity 0.25s ease, transform 0.25s ease';
        card.style.opacity = '0';
        card.style.transform = 'scale(0.94)';

        setTimeout(() => {
          card.remove();
          const remaining = this.refs.grid.querySelectorAll('product-card, .product-card, article');
          if (remaining.length === 0) {
            this.refs.empty.hidden = false;
          }
        }, 250);
      }
    } else if (added && changedHandle) {
      // If an item was added while on the wishlist page, fetch and prepend just that one card
      const existing =
        this.refs.grid.querySelector(`product-card[data-handle="${changedHandle}"]`) ||
        this.refs.grid.querySelector(`[data-handle="${changedHandle}"]`);

      if (!existing) {
        this.fetchProductCard(changedHandle).then((cardEl) => {
          if (cardEl) {
            this.refs.grid.prepend(cardEl);
            this.refs.empty.hidden = true;
          }
        });
      }
    }
  };

  load = async () => {
    const handles = getWishlist();

    if (handles.length === 0) {
      this.refs.grid.replaceChildren();
      this.refs.empty.hidden = false;
      return;
    }

    this.refs.empty.hidden = true;

    const results = await Promise.allSettled(handles.map((handle) => this.fetchProductCard(handle)));
    const fragment = document.createDocumentFragment();

    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value) {
        fragment.append(result.value);
      } else {
        // The product no longer resolves (deleted/unpublished) — stop tracking it.
        removeFromWishlist(handles[index]);
      }
    });

    this.refs.grid.replaceChildren(fragment);
    this.refs.empty.hidden = this.refs.grid.children.length > 0;
  };

  /**
   * Fetches the full product-card HTML via Section Rendering API.
   * @param {string} handle
   * @returns {Promise<HTMLElement | null>}
   */
  async fetchProductCard(handle) {
    try {
      const response = await fetch(`/products/${handle}?section_id=wishlist-product-card`);
      if (!response.ok) return null;
      const html = await response.text();

      const template = document.createElement('template');
      template.innerHTML = html;

      // Ensure any script tags inside the section HTML are executed (e.g. wishlist-button or product-card inits)
      const scripts = template.content.querySelectorAll('script');
      scripts.forEach((oldScript) => {
        if (oldScript.src) {
          if (!document.querySelector(`script[src="${oldScript.src}"]`)) {
            const newScript = document.createElement('script');
            Array.from(oldScript.attributes).forEach((attr) => newScript.setAttribute(attr.name, attr.value));
            document.head.appendChild(newScript);
          }
        } else if (oldScript.textContent && !oldScript.type) {
          try {
            (new Function(oldScript.textContent))();
          } catch (_e) {}
        }
      });

      const card = template.content.querySelector('product-card');
      if (!card) return null;

      card.setAttribute('data-handle', handle);
      return card;
    } catch (_error) {
      return null;
    }
  }
}

if (!customElements.get('wishlist-grid')) {
  customElements.define('wishlist-grid', WishlistGrid);
}
